import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService, Theme } from '../../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

/**
 * Chart mode - determines how bars are displayed
 */
export type BarChartMode = 'single' | 'multiple' | 'stacked' | 'combo';

/**
 * Bar orientation
 */
export type BarOrientation = 'vertical' | 'horizontal';

/**
 * Data point structure for each series
 */
export interface IBarChartDataPoint {
  /** Category/label for the bar */
  category: string;
  /** Date string (optional, for time-based charts) */
  date?: string;
  /** Numeric value for the data point */
  value: number;
  /** Optional label for tooltip */
  label?: string;
  /** Optional additional data for tooltip */
  metadata?: Record<string, any>;
}

/**
 * Configuration for each series in the chart
 */
export interface IBarChartSeries {
  /** Unique identifier for the series */
  key: string;
  /** Display name for the legend */
  name: string;
  /** Data points for the series */
  data: IBarChartDataPoint[];
  /** Bar color (optional - uses theme defaults if not provided) */
  color?: string;
  /** Bar opacity (0-1, default: 1) */
  opacity?: number;
  /** Bar fill style: 'solid' | 'gradient' (default: 'solid') */
  barFillStyle?: 'solid' | 'gradient';
  /** Gradient start color (defaults to series color if not provided) */
  gradientStartColor?: string;
  /** Gradient end color (defaults to series color with reduced opacity if not provided) */
  gradientEndColor?: string;
  /** Gradient start opacity (0-1, default: uses series opacity) */
  gradientStartOpacity?: number;
  /** Gradient end opacity (0-1, default: 0.1) */
  gradientEndOpacity?: number;
  /** Gradient direction: 'vertical' | 'horizontal' (default: 'vertical') */
  gradientDirection?: 'vertical' | 'horizontal';
  /** Whether this series should be shown as a line (for combo charts) */
  isLine?: boolean;
  /** Line color (for combo charts) */
  lineColor?: string;
  /** Line stroke width (default: 2) */
  lineStrokeWidth?: number;
  /** Line style - 'solid' | 'dashed' | 'dotted' (default: 'solid') */
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  /** Whether to show data points on line (default: false) */
  showLinePoints?: boolean;
  /** Point radius (default: 4) */
  pointRadius?: number;
  /** Y-axis side for this series: 'left' | 'right' (default: 'left') */
  yAxisSide?: 'left' | 'right';
  /** Value format for this series */
  valueFormat?: 'number' | 'currency' | 'percent';
}

/**
 * Chart theme configuration
 */
export interface IBarChartTheme {
  /** Background color */
  backgroundColor?: string;
  /** Grid line color */
  gridColor?: string;
  /** Axis line color */
  axisColor?: string;
  /** Axis label color */
  axisLabelColor?: string;
  /** Tooltip background color */
  tooltipBg?: string;
  /** Tooltip border color */
  tooltipBorder?: string;
  /** Tooltip text color */
  tooltipText?: string;
  /** Hover line color */
  hoverLineColor?: string;
  /** Default colors for series (used when series colors not provided) */
  seriesColors?: string[];
}

/**
 * Margin configuration
 */
export interface IBarChartMargins {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

/**
 * Legend configuration
 */
export interface IBarLegendConfig {
  /** Whether to show the legend */
  show?: boolean;
  /** Legend position */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Legend item alignment */
  align?: 'start' | 'center' | 'end';
  /** Gap between legend items */
  itemGap?: number;
}

/**
 * Axis configuration
 */
export interface IBarAxisConfig {
  /** Whether to show the axis */
  show?: boolean;
  /** Whether to show axis line */
  showAxisLine?: boolean;
  /** Whether to show grid lines */
  showGrid?: boolean;
  /** Grid line style */
  gridStyle?: 'solid' | 'dashed' | 'dotted';
  /** Whether to show only first and last labels */
  showOnlyFirstLast?: boolean;
  /** Whether to show all labels */
  showAllLabels?: boolean;
  /** Custom tick format function */
  tickFormat?: (value: any) => string;
  /** Number of ticks (for Y axis) */
  tickCount?: number;
  /** Font size for labels */
  fontSize?: number;
  /** Label rotation angle (for X axis) */
  labelRotation?: number;
}

/**
 * Tooltip configuration
 */
export interface IBarTooltipConfig {
  /** Whether to show tooltips */
  show?: boolean;
  /** Whether to show hover line */
  showHoverLine?: boolean;
  /** Whether to show total of all series values */
  showTotal?: boolean;
  /** Custom label for total row */
  totalLabel?: string;
  /** Value format type */
  valueFormat?: 'number' | 'currency' | 'percent';
  /** Custom value formatter */
  valueFormatter?: (value: number, series: IBarChartSeries, point: IBarChartDataPoint) => string;
  /** Custom category formatter */
  categoryFormatter?: (category: string) => string;
}

/**
 * Animation configuration
 */
export interface IBarAnimationConfig {
  /** Whether to animate the chart */
  enabled?: boolean;
  /** Animation duration in ms */
  duration?: number;
  /** Animation easing function */
  easing?: 'linear' | 'ease' | 'easeIn' | 'easeOut' | 'easeInOut' | 'bounce' | 'elastic';
}

@Component({
  selector: 'app-comprehensive-bar-chart',
  templateUrl: './comprehensive-bar-chart.component.html',
  styleUrls: ['./comprehensive-bar-chart.component.scss'],
  providers: [CurrencyPipe],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule],
})
export class ComprehensiveBarChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  // ============ DATA INPUTS ============
  /** Series data for the chart */
  @Input() series: IBarChartSeries[] = [];
  /** Chart mode */
  @Input() mode: BarChartMode = 'multiple';
  /** Bar orientation */
  @Input() orientation: BarOrientation = 'vertical';

  // ============ CHART TITLE ============
  /** Chart title */
  @Input() title: string = '';
  /** Chart subtitle/description */
  @Input() subtitle: string = '';

  // ============ TEXT STYLE CLASSES (Tailwind) ============
  /** Custom Tailwind classes for title */
  @Input() titleClass: string = '';
  /** Custom Tailwind classes for subtitle */
  @Input() subtitleClass: string = '';
  /** Custom Tailwind classes for header container */
  @Input() headerClass: string = '';
  /** Custom Tailwind classes for legend container */
  @Input() legendClass: string = '';
  /** Custom Tailwind classes for legend labels */
  @Input() legendLabelClass: string = '';

  // ============ DIMENSIONS ============
  /** Custom margins */
  @Input() margins: IBarChartMargins = {};
  /** Chart height (in pixels or 'auto') */
  @Input() height: number | 'auto' = 'auto';

  // ============ BAR STYLING ============
  /** Bar width (percentage of available space, 0-1) */
  @Input() barWidth: number = 0.8;
  /** Gap between bars in a group */
  @Input() barGap: number = 0.1;
  /** Gap between bar groups */
  @Input() groupGap: number = 0.2;
  /** Bar border radius */
  @Input() barRadius: number = 4;
  /** Whether to show bar values on top */
  @Input() showBarValues: boolean = false;
  /** Bar value format */
  @Input() barValueFormat: 'number' | 'currency' | 'percent' = 'number';

  // ============ APPEARANCE ============
  /** Custom theme configuration */
  @Input() theme: IBarChartTheme = {};
  /** Whether to animate the chart */
  @Input() animation: IBarAnimationConfig = { enabled: true, duration: 750, easing: 'easeOut' };

  // ============ AXES CONFIGURATION ============
  /** X-axis configuration */
  @Input() xAxis: IBarAxisConfig = { show: true, showGrid: false, showAxisLine: true };
  /** Y-axis configuration */
  @Input() yAxis: IBarAxisConfig = { show: true, showGrid: true, showAxisLine: false, gridStyle: 'dashed' };
  /** Y-axis configuration (right, for dual axis) */
  @Input() yAxisRight: IBarAxisConfig = { show: false, showGrid: false, showAxisLine: false };

  // ============ LEGEND CONFIGURATION ============
  /** Legend configuration */
  @Input() legend: IBarLegendConfig = { show: true, position: 'bottom', align: 'center' };

  // ============ TOOLTIP CONFIGURATION ============
  /** Tooltip configuration */
  @Input() tooltip: IBarTooltipConfig = { show: true, showHoverLine: true, valueFormat: 'number' };

  // ============ CARD WRAPPER ============
  /** Whether to show card wrapper */
  @Input() showCard: boolean = true;
  /** Card CSS classes */
  @Input() cardClass: string = '';
  /** Custom card padding */
  @Input() cardPadding: string = '1.5rem';

  // ============ INTERNAL STATE ============
  @ViewChild('chartContainer', { static: false })
  private chartContainer!: ElementRef;

  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  private themeService = inject(ThemeService);
  private isViewInitialized = false;
  private hasRenderedOnce = false;

  constructor(private currencyPipe: CurrencyPipe) { }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['series'] || changes['mode'] || changes['orientation'] || changes['theme'] ||
      changes['xAxis'] || changes['yAxis'] || changes['yAxisRight'] || changes['barWidth'] || changes['barGap']) &&
      this.isViewInitialized && this.chartContainer?.nativeElement) {
      this.createChart();
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    this.currentTheme = this.themeService.getTheme();

    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      if (this.chartContainer?.nativeElement) {
        this.createChart();
      }
    });

    setTimeout(() => {
      if (this.chartContainer?.nativeElement) {
        this.resizeObserver = new ResizeObserver(() => {
          this.createChart();
        });
        this.resizeObserver.observe(this.chartContainer.nativeElement);
        this.createChart();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  /**
   * Get theme-aware colors with user overrides
   */
  private getThemeColors(): Required<IBarChartTheme> {
    const isDark = this.currentTheme === 'dark';

    const defaultColors = {
      backgroundColor: isDark ? 'transparent' : 'transparent',
      gridColor: isDark ? '#47474e' : '#e5e7eb',
      axisColor: isDark ? '#47474e' : '#d1d5db',
      axisLabelColor: isDark ? '#a1a1aa' : '#6b7280',
      tooltipBg: isDark ? '#1e1e21' : '#ffffff',
      tooltipBorder: isDark ? '#2e2e33' : '#e5e7eb',
      tooltipText: isDark ? '#f4f4f5' : '#111827',
      hoverLineColor: isDark ? '#85858e' : '#9ca3af',
      seriesColors: isDark
        ? ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316']
        : ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'],
    };

    return {
      backgroundColor: this.theme.backgroundColor ?? defaultColors.backgroundColor,
      gridColor: this.theme.gridColor ?? defaultColors.gridColor,
      axisColor: this.theme.axisColor ?? defaultColors.axisColor,
      axisLabelColor: this.theme.axisLabelColor ?? defaultColors.axisLabelColor,
      tooltipBg: this.theme.tooltipBg ?? defaultColors.tooltipBg,
      tooltipBorder: this.theme.tooltipBorder ?? defaultColors.tooltipBorder,
      tooltipText: this.theme.tooltipText ?? defaultColors.tooltipText,
      hoverLineColor: this.theme.hoverLineColor ?? defaultColors.hoverLineColor,
      seriesColors: this.theme.seriesColors ?? defaultColors.seriesColors,
    };
  }

  /**
   * Get animation easing function
   */
  private getEasingFunction(): (t: number) => number {
    const easingMap: Record<string, (t: number) => number> = {
      linear: d3.easeLinear,
      ease: d3.easeQuadInOut,
      easeIn: d3.easeQuadIn,
      easeOut: d3.easeQuadOut,
      easeInOut: d3.easeQuadInOut,
      bounce: d3.easeBounceOut,
      elastic: d3.easeElasticOut,
    };
    return easingMap[this.animation.easing || 'easeOut'] || d3.easeQuadOut;
  }

  /**
   * Format value based on tooltip configuration
   */
  private formatValue(value: number, format?: 'number' | 'currency' | 'percent'): string {
    const formatType = format || this.tooltip.valueFormat || 'number';

    switch (formatType) {
      case 'currency':
        return this.currencyPipe.transform(value, 'USD', 'symbol', '1.0-2') || `$${value}`;
      case 'percent':
        return `${value.toFixed(1)}%`;
      default:
        return value.toLocaleString('en-US');
    }
  }

  /**
   * Calculate the width needed for Y-axis labels
   */
  private getYAxisWidth(labels: string[]): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 50;
    context.font = `${this.yAxis.fontSize || 12}px system-ui, -apple-system, sans-serif`;
    return Math.max(...labels.map((label) => context.measureText(label).width)) + 10;
  }

  /**
   * Create rounded rectangle path
   */
  private createRoundedRect(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    orientation: BarOrientation
  ): string {
    if (orientation === 'horizontal') {
      // Horizontal bars: rounded on right side
      return `
        M ${x} ${y}
        L ${x + width - radius} ${y}
        Q ${x + width} ${y} ${x + width} ${y + radius}
        L ${x + width} ${y + height - radius}
        Q ${x + width} ${y + height} ${x + width - radius} ${y + height}
        L ${x} ${y + height}
        Z
      `;
    } else {
      // Vertical bars: rounded on top
      return `
        M ${x} ${y + height}
        L ${x} ${y + radius}
        Q ${x} ${y} ${x + radius} ${y}
        L ${x + width - radius} ${y}
        Q ${x + width} ${y} ${x + width} ${y + radius}
        L ${x + width} ${y + height}
        Z
      `;
    }
  }

  /**
   * Create the D3 chart
   */
  private createChart(): void {
    if (!this.chartContainer?.nativeElement) {
      return;
    }

    const container = this.chartContainer.nativeElement;

    if (!this.series || this.series.length === 0) {
      d3.select(container).selectAll('*').remove();
      return;
    }

    d3.select(container).selectAll('*').remove();

    const colors = this.getThemeColors();
    const isHorizontal = this.orientation === 'horizontal';

    // Process all series data
    const processedSeries = this.series.map((s, index) => ({
      ...s,
      color: s.color || colors.seriesColors[index % colors.seriesColors.length],
      opacity: s.opacity ?? 1,
      isLine: s.isLine ?? false,
      lineColor: s.lineColor || s.color || colors.seriesColors[index % colors.seriesColors.length],
      lineStrokeWidth: s.lineStrokeWidth ?? 2,
      lineStyle: s.lineStyle ?? 'solid',
      showLinePoints: s.showLinePoints ?? true,
      pointRadius: s.pointRadius ?? 4,
      yAxisSide: s.yAxisSide || 'left',
      valueFormat: s.valueFormat || this.tooltip.valueFormat || 'number',
    }));

    // Get all unique categories
    const allCategories = new Set<string>();
    processedSeries.forEach(series => {
      series.data.forEach(point => {
        allCategories.add(point.category);
      });
    });
    const categories = Array.from(allCategories);

    // Separate series by Y-axis side
    const leftAxisSeries = processedSeries.filter(s => s.yAxisSide === 'left' || !s.yAxisSide);
    const rightAxisSeries = processedSeries.filter(s => s.yAxisSide === 'right');

    // Calculate value ranges for each axis
    const allLeftValues = leftAxisSeries.flatMap(s => s.data.map(d => d.value));
    const allRightValues = rightAxisSeries.flatMap(s => s.data.map(d => d.value));

    const maxLeftValue = d3.max(allLeftValues) ?? 0;
    const minLeftValue = this.mode === 'stacked'
      ? Math.min(0, d3.min(leftAxisSeries.flatMap(s =>
        s.data.reduce((acc, d, i) => {
          const prev = acc[i - 1] || 0;
          acc.push(prev + d.value);
          return acc;
        }, [] as number[])
      )) || 0)
      : Math.min(0, d3.min(allLeftValues) || 0);

    const maxRightValue = d3.max(allRightValues) ?? 0;
    const minRightValue = Math.min(0, d3.min(allRightValues) || 0);

    // Calculate Y-axis labels for width estimation
    const yTempScale = d3.scaleLinear().domain([minLeftValue, maxLeftValue]).nice();
    const yLabels = yTempScale.ticks(this.yAxis.tickCount || 6).map(d => this.formatValue(d, this.tooltip.valueFormat));
    const yAxisWidth = this.yAxis.show ? this.getYAxisWidth(yLabels) : 0;

    // Calculate right Y-axis width if needed
    let yAxisRightWidth = 0;
    if (rightAxisSeries.length > 0 && this.yAxisRight.show) {
      const yRightTempScale = d3.scaleLinear().domain([minRightValue, maxRightValue]).nice();
      const yRightLabels = yRightTempScale.ticks(this.yAxisRight.tickCount || 6).map(d => this.formatValue(d, 'percent'));
      yAxisRightWidth = this.getYAxisWidth(yRightLabels);
    }

    // Calculate dimensions
    const width = container.clientWidth;
    const height = container.clientHeight;
    const marginTop = this.margins.top ?? 20;
    const marginRight = this.margins.right ?? (yAxisRightWidth > 0 ? yAxisRightWidth + 25 : 20);
    const marginBottom = this.margins.bottom ?? (this.xAxis.show ? 40 : 20);
    const marginLeft = this.margins.left ?? (this.yAxis.show ? yAxisWidth + 15 : 20);

    // Create scales
    const chartWidth = width - marginLeft - marginRight;
    const chartHeight = height - marginTop - marginBottom;

    let xScale: d3.ScaleBand<string> | d3.ScaleLinear<number, number>;
    let yScale: d3.ScaleLinear<number, number> | d3.ScaleBand<string>;
    let yScaleRight: d3.ScaleLinear<number, number> | null = null;

    if (isHorizontal) {
      xScale = d3.scaleLinear()
        .domain([minLeftValue, maxLeftValue])
        .range([marginLeft, width - marginRight])
        .nice();

      yScale = d3.scaleBand()
        .domain(categories)
        .range([marginTop, height - marginBottom])
        .paddingInner(this.groupGap)
        .paddingOuter(0.1); // Add outer padding to prevent clipping
    } else {
      xScale = d3.scaleBand()
        .domain(categories)
        .range([marginLeft, width - marginRight])
        .paddingInner(this.groupGap)
        .paddingOuter(0.1); // Add outer padding to prevent clipping

      yScale = d3.scaleLinear()
        .domain([minLeftValue, maxLeftValue])
        .range([height - marginBottom, marginTop])
        .nice();

      // Create right Y-axis scale if needed
      if (rightAxisSeries.length > 0 && this.yAxisRight.show) {
        yScaleRight = d3.scaleLinear()
          .domain([minRightValue, maxRightValue])
          .range([height - marginBottom, marginTop])
          .nice();
      }
    }

    // Create SVG
    const svg = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('overflow', 'visible');

    // Draw Y-axis grid lines
    if (this.yAxis.showGrid && !isHorizontal) {
      const yTicks = (yScale as d3.ScaleLinear<number, number>).ticks?.(this.yAxis.tickCount || 6) || [];
      svg.append('g')
        .attr('class', 'y-grid')
        .selectAll('line')
        .data(yTicks)
        .join('line')
        .attr('x1', marginLeft)
        .attr('x2', width - marginRight)
        .attr('y1', d => (yScale as d3.ScaleLinear<number, number>)(d))
        .attr('y2', d => (yScale as d3.ScaleLinear<number, number>)(d))
        .attr('stroke', colors.gridColor)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', this.yAxis.gridStyle === 'dashed' ? '4,4' : this.yAxis.gridStyle === 'dotted' ? '2,2' : '0');
    }

    // Draw X-axis grid lines
    if (this.xAxis.showGrid && !isHorizontal) {
      const xTicks = (xScale as d3.ScaleBand<string>).domain();
      svg.append('g')
        .attr('class', 'x-grid')
        .selectAll('line')
        .data(xTicks)
        .join('line')
        .attr('x1', d => (xScale as d3.ScaleBand<string>)(d)! + (xScale as d3.ScaleBand<string>).bandwidth() / 2)
        .attr('x2', d => (xScale as d3.ScaleBand<string>)(d)! + (xScale as d3.ScaleBand<string>).bandwidth() / 2)
        .attr('y1', marginTop)
        .attr('y2', height - marginBottom)
        .attr('stroke', colors.gridColor)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', this.xAxis.gridStyle === 'dashed' ? '4,4' : this.xAxis.gridStyle === 'dotted' ? '2,2' : '0');
    }

    // Calculate bar dimensions
    const barSeries = processedSeries.filter(s => !s.isLine);
    
    // Create nested scale for grouped bars
    const groupDomain = barSeries.map(s => s.key);
    let groupScale: d3.ScaleBand<string>;

    if (isHorizontal) {
      const bandWidth = (yScale as d3.ScaleBand<string>).bandwidth();
      groupScale = d3.scaleBand()
        .domain(groupDomain)
        .range([bandWidth * (1 - this.barWidth) / 2, bandWidth * (1 + this.barWidth) / 2])
        .paddingInner(this.barGap);
    } else {
      const bandWidth = (xScale as d3.ScaleBand<string>).bandwidth();
      groupScale = d3.scaleBand()
        .domain(groupDomain)
        .range([bandWidth * (1 - this.barWidth) / 2, bandWidth * (1 + this.barWidth) / 2])
        .paddingInner(this.barGap);
    }

    const barWidth = groupScale.bandwidth();

    // Add defs for gradients (after barSeries is defined)
    const defs = svg.select('defs').empty() ? svg.append('defs') : svg.select('defs');

    // Create gradient for each bar series (only if gradient fill is requested)
    barSeries.forEach((series) => {
      if (series.barFillStyle === 'gradient') {
        const gradientId = `bar-gradient-${series.key}`;
        const direction = series.gradientDirection || 'vertical';

        const gradient = defs.append('linearGradient')
          .attr('id', gradientId)
          .attr('x1', direction === 'horizontal' ? '0%' : '0%')
          .attr('y1', direction === 'horizontal' ? '0%' : '0%')
          .attr('x2', direction === 'horizontal' ? '100%' : '0%')
          .attr('y2', direction === 'horizontal' ? '0%' : '100%');

        const startColor = series.gradientStartColor || series.color;
        const endColor = series.gradientEndColor || series.color;
        const startOpacity = series.gradientStartOpacity ?? series.opacity ?? 1;
        const endOpacity = series.gradientEndOpacity ?? Math.max(0.1, (series.opacity ?? 1) * 0.3);

        gradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', startColor)
          .attr('stop-opacity', startOpacity);

        gradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', endColor)
          .attr('stop-opacity', endOpacity);
      }
    });

    // Draw bars
    const barsGroup = svg.append('g').attr('class', 'bars');

    if (this.mode === 'stacked') {
      // Stacked bars
      barSeries.forEach((series, seriesIndex) => {
        const stackData = categories.map(category => {
          let stackValue = 0;
          for (let i = 0; i <= seriesIndex; i++) {
            const point = barSeries[i].data.find(d => d.category === category);
            if (point) stackValue += point.value;
          }
          const prevStackValue = seriesIndex > 0
            ? categories.map(cat => {
              let sum = 0;
              for (let i = 0; i < seriesIndex; i++) {
                const pt = barSeries[i].data.find(d => d.category === cat);
                if (pt) sum += pt.value;
              }
              return sum;
            })[categories.indexOf(category)]
            : 0;

          const point = series.data.find(d => d.category === category);
          return {
            category,
            value: point?.value || 0,
            y0: prevStackValue,
            y1: stackValue,
          };
        });

        stackData.forEach((d, i) => {
          const category = categories[i];
          let x: number, y: number, w: number, h: number;

          if (isHorizontal) {
            const yPos = (yScale as d3.ScaleBand<string>)(category)!;
            const currentBandWidth = (yScale as d3.ScaleBand<string>).bandwidth();
            x = (xScale as d3.ScaleLinear<number, number>)(d.y0);
            y = yPos + (currentBandWidth * (1 - this.barWidth)) / 2;
            w = (xScale as d3.ScaleLinear<number, number>)(d.y1) - x;
            h = currentBandWidth * this.barWidth;
          } else {
            const xPos = (xScale as d3.ScaleBand<string>)(category)!;
            const currentBandWidth = (xScale as d3.ScaleBand<string>).bandwidth();
            y = (yScale as d3.ScaleLinear<number, number>)(d.y1);
            x = xPos + (currentBandWidth * (1 - this.barWidth)) / 2;
            h = (yScale as d3.ScaleLinear<number, number>)(d.y0) - y;
            w = currentBandWidth * this.barWidth;
          }

          const rect = barsGroup.append('path')
            .attr('d', this.createRoundedRect(x, y, w, h, this.barRadius, this.orientation))
            .attr('fill', series.barFillStyle === 'gradient' ? `url(#bar-gradient-${series.key})` : series.color)
            .attr('opacity', series.barFillStyle === 'gradient' ? 1 : series.opacity)
            .attr('class', `bar-${series.key}-${category}`)
            .style('cursor', 'pointer');

          if (this.animation.enabled && !this.hasRenderedOnce) {
            if (isHorizontal) {
              rect
                .attr('opacity', 0)
                .transition()
                .duration(this.animation.duration || 750)
                .ease(this.getEasingFunction())
                .attr('opacity', series.opacity);
            } else {
              const finalHeight = h;
              rect
                .attr('h', 0)
                .attr('y', (yScale as d3.ScaleLinear<number, number>)(d.y0))
                .transition()
                .duration(this.animation.duration || 750)
                .ease(this.getEasingFunction())
                .attr('d', this.createRoundedRect(x, y, w, finalHeight, this.barRadius, this.orientation));
            }
          }

          // Add bar value label
          if (this.showBarValues && d.value !== 0) {
            const labelX = isHorizontal
              ? (xScale as d3.ScaleLinear<number, number>)(d.y0 + d.value / 2)
              : x + w / 2;
            const labelY = isHorizontal
              ? y + h / 2
              : y + h / 2;

            barsGroup.append('text')
              .attr('x', labelX)
              .attr('y', labelY)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'middle')
              .attr('fill', colors.axisLabelColor)
              .attr('font-size', '11px')
              .attr('font-weight', '500')
              .text(this.formatValue(d.value, this.barValueFormat));
          }
        });
      });
    } else {
      // Grouped or single bars
      barSeries.forEach((series) => {
        series.data.forEach((point) => {
          const category = point.category;
          let x: number, y: number, w: number, h: number;

          if (isHorizontal) {
            const yPos = (yScale as d3.ScaleBand<string>)(category)!;
            const groupOffset = groupScale(series.key)!;
            const baseX = (xScale as d3.ScaleLinear<number, number>)(0);
            const valueX = (xScale as d3.ScaleLinear<number, number>)(point.value);

            x = Math.min(baseX, valueX);
            y = yPos + groupOffset;
            w = Math.abs(valueX - baseX);
            h = barWidth;
            
            if (point.value === 0) {
              w = this.barRadius || 4;
            }
          } else {
            const xPos = (xScale as d3.ScaleBand<string>)(category)!;
            const groupOffset = groupScale(series.key)!;
            const baseY = (yScale as d3.ScaleLinear<number, number>)(0);
            const valueY = (yScale as d3.ScaleLinear<number, number>)(point.value);

            x = xPos + groupOffset;
            y = Math.min(baseY, valueY);
            w = barWidth;
            h = Math.abs(valueY - baseY);
            
            if (point.value === 0) {
              h = this.barRadius || 4;
              y = baseY - h;
            }
          }

          const rect = barsGroup.append('path')
            .attr('d', this.createRoundedRect(x, y, w, h, this.barRadius, this.orientation))
            .attr('fill', series.barFillStyle === 'gradient' ? `url(#bar-gradient-${series.key})` : series.color)
            .attr('opacity', series.barFillStyle === 'gradient' ? 1 : series.opacity)
            .attr('class', `bar-${series.key}-${category}`)
            .style('cursor', 'pointer');

          if (this.animation.enabled && !this.hasRenderedOnce) {
            if (isHorizontal) {
              rect
                .attr('opacity', 0)
                .transition()
                .duration(this.animation.duration || 750)
                .ease(this.getEasingFunction())
                .attr('opacity', series.opacity);
            } else {
              const finalHeight = h;
              rect
                .attr('h', 0)
                .attr('y', (yScale as d3.ScaleLinear<number, number>)(0))
                .transition()
                .duration(this.animation.duration || 750)
                .ease(this.getEasingFunction())
                .attr('d', this.createRoundedRect(x, y, w, finalHeight, this.barRadius, this.orientation));
            }
          }

          // Add bar value label
          if (this.showBarValues && point.value !== 0) {
            const labelX = isHorizontal
              ? x + w / 2
              : x + w / 2;
            const labelY = isHorizontal
              ? y + h / 2
              : y + h / 2;

            barsGroup.append('text')
              .attr('x', labelX)
              .attr('y', labelY)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'middle')
              .attr('fill', colors.axisLabelColor)
              .attr('font-size', '11px')
              .attr('font-weight', '500')
              .text(this.formatValue(point.value, this.barValueFormat));
          }
        });
      });
    }

    // Draw lines for combo charts
    const lineSeries = processedSeries.filter(s => s.isLine);
    if (lineSeries.length > 0 && this.mode === 'combo') {
      const linesGroup = svg.append('g').attr('class', 'lines');

      lineSeries.forEach((series) => {
        const lineData = categories.map(category => {
          const point = series.data.find(d => d.category === category);
          return {
            category,
            value: point?.value || 0,
            x: isHorizontal
              ? (xScale as d3.ScaleLinear<number, number>)(point?.value || 0)
              : (xScale as d3.ScaleBand<string>)(category)! + (xScale as d3.ScaleBand<string>).bandwidth() / 2,
            y: isHorizontal
              ? (yScale as d3.ScaleBand<string>)(category)! + (yScale as d3.ScaleBand<string>).bandwidth() / 2
              : (series.yAxisSide === 'right' && yScaleRight ? yScaleRight : yScale as d3.ScaleLinear<number, number>)(point?.value || 0),
          };
        });

        const lineGenerator = isHorizontal
          ? d3.line<any>().x(d => d.x).y(d => d.y).curve(d3.curveMonotoneX)
          : d3.line<any>().x(d => d.x).y(d => d.y).curve(d3.curveMonotoneX);

        const linePath = linesGroup.append('path')
          .datum(lineData)
          .attr('fill', 'none')
          .attr('stroke', series.lineColor)
          .attr('stroke-width', series.lineStrokeWidth)
          .attr('stroke-linecap', 'round')
          .attr('stroke-linejoin', 'round')
          .attr('d', lineGenerator);

        const getDashArray = () => {
          if (series.lineStyle === 'dashed') return '5,5';
          if (series.lineStyle === 'dotted') return '2,2';
          return 'none';
        };

        if (this.animation.enabled && !this.hasRenderedOnce) {
          const totalLength = (linePath.node() as SVGPathElement)?.getTotalLength() || 0;
          linePath
            .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
            .attr('stroke-dashoffset', totalLength)
            .transition()
            .duration(this.animation.duration || 750)
            .ease(this.getEasingFunction())
            .attr('stroke-dashoffset', 0)
            .on('end', function () {
              d3.select(this).attr('stroke-dasharray', getDashArray());
            });
        } else {
          linePath.attr('stroke-dasharray', getDashArray());
        }

        // Draw line points
        if (series.showLinePoints) {
          linesGroup.selectAll(`.point-${series.key}`)
            .data(lineData.filter(d => d.value !== 0))
            .join('circle')
            .attr('class', `point-${series.key}`)
            .attr('cx', d => d.x)
            .attr('cy', d => {
              if (isHorizontal) return d.y;
              return series.yAxisSide === 'right' && yScaleRight ? yScaleRight(d.value) : d.y;
            })
            .attr('r', series.pointRadius)
            .attr('fill', series.lineColor)
            .attr('stroke', '#ffffff')
            .attr('stroke-width', 2);
        }
      });
    }

    // Draw X-axis
    if (this.xAxis.show) {
      let xTickValues: string[];

      if (this.xAxis.showOnlyFirstLast) {
        xTickValues = [categories[0], categories[categories.length - 1]];
      } else if (this.xAxis.showAllLabels) {
        xTickValues = categories;
      } else {
        const maxTicks = Math.min(this.xAxis.tickCount || 8, categories.length);
        if (categories.length <= maxTicks) {
          xTickValues = categories;
        } else {
          const step = Math.ceil(categories.length / maxTicks);
          xTickValues = categories.filter((_, i) => i % step === 0);
          if (xTickValues[xTickValues.length - 1] !== categories[categories.length - 1]) {
            xTickValues.push(categories[categories.length - 1]);
          }
        }
      }
      if (this.xAxis.showOnlyFirstLast && categories.length > 0) {
        xTickValues = [categories[0], categories[categories.length - 1]];
      }

      const xAxisG = svg.append('g')
        .attr('class', 'x-axis')
        .attr('transform', isHorizontal
          ? `translate(0,${height - marginBottom})`
          : `translate(0,${height - marginBottom})`);

      if (this.xAxis.showAxisLine) {
        xAxisG.append('line')
          .attr('x1', marginLeft)
          .attr('x2', width - marginRight)
          .attr('y1', 0)
          .attr('y2', 0)
          .attr('stroke', colors.axisColor);
      }

      xAxisG.selectAll('.x-tick')
        .data(xTickValues)
        .join('g')
        .attr('class', 'x-tick')
        .attr('transform', d => isHorizontal
          ? `translate(${(xScale as d3.ScaleLinear<number, number>)(0)},0)`
          : `translate(${(xScale as d3.ScaleBand<string>)(d)! + (xScale as d3.ScaleBand<string>).bandwidth() / 2},0)`)
        .call(g => {
          g.append('text')
            .attr('y', 20)
            .attr('text-anchor', 'middle')
            .attr('fill', colors.axisLabelColor)
            .attr('font-size', this.xAxis.fontSize || 12)
            .attr('transform', this.xAxis.labelRotation ? `rotate(${this.xAxis.labelRotation})` : '')
            .text(d => this.xAxis.tickFormat ? this.xAxis.tickFormat(d) : (this.tooltip.categoryFormatter ? this.tooltip.categoryFormatter(d) : d));
        });
    }

    // Draw Y-axis
    if (this.yAxis.show) {
      const yTicks = (yScale as d3.ScaleLinear<number, number>).ticks?.(this.yAxis.tickCount || 6) || [];
      let displayTicks = yTicks;

      if (this.yAxis.showOnlyFirstLast && yTicks.length > 0) {
        displayTicks = [yTicks[0], yTicks[yTicks.length - 1]];
      }

      const yAxisG = svg.append('g')
        .attr('class', 'y-axis')
        .attr('transform', isHorizontal
          ? `translate(${marginLeft},0)`
          : `translate(${marginLeft},0)`);

      if (this.yAxis.showAxisLine) {
        yAxisG.append('line')
          .attr('y1', marginTop)
          .attr('y2', height - marginBottom)
          .attr('x1', 0)
          .attr('x2', 0)
          .attr('stroke', colors.axisColor);
      }

      yAxisG.selectAll('.y-tick')
        .data(displayTicks)
        .join('g')
        .attr('class', 'y-tick')
        .attr('transform', d => isHorizontal
          ? `translate(0,${(yScale as d3.ScaleBand<string>)(categories[0])!})`
          : `translate(0,${(yScale as d3.ScaleLinear<number, number>)(d)})`)
        .call(g => {
          g.append('text')
            .attr('x', isHorizontal ? 0 : -12)
            .attr('dy', '0.32em')
            .attr('text-anchor', isHorizontal ? 'middle' : 'end')
            .attr('fill', colors.axisLabelColor)
            .attr('font-size', this.yAxis.fontSize || 12)
            .text(d => this.yAxis.tickFormat ? this.yAxis.tickFormat(d) : this.formatValue(d, this.tooltip.valueFormat));
        });
    }

    // Draw Y-axis (right, for dual axis)
    if (yScaleRight && this.yAxisRight.show) {
      const yTicks = yScaleRight.ticks(this.yAxisRight.tickCount || 6);
      let displayTicks = yTicks;

      if (this.yAxisRight.showOnlyFirstLast && yTicks.length > 0) {
        displayTicks = [yTicks[0], yTicks[yTicks.length - 1]];
      }

      const yAxisRightG = svg.append('g')
        .attr('class', 'y-axis-right')
        .attr('transform', `translate(${width - marginRight},0)`);

      if (this.yAxisRight.showAxisLine) {
        yAxisRightG.append('line')
          .attr('y1', marginTop)
          .attr('y2', height - marginBottom)
          .attr('x1', 0)
          .attr('x2', 0)
          .attr('stroke', colors.axisColor);
      }

      yAxisRightG.selectAll('.y-tick-right')
        .data(displayTicks)
        .join('g')
        .attr('class', 'y-tick-right')
        .attr('transform', d => `translate(0,${yScaleRight(d)})`)
        .call(g => {
          g.append('text')
            .attr('x', 15)
            .attr('dy', '0.32em')
            .attr('text-anchor', 'start')
            .attr('fill', colors.axisLabelColor)
            .attr('font-size', this.yAxisRight.fontSize || 12)
            .text(d => this.yAxisRight.tickFormat ? this.yAxisRight.tickFormat(d) : this.formatValue(d, 'percent'));
        });
    }

    // Tooltip and hover interactions
    if (this.tooltip.show) {
      const tooltip = d3.select(container)
        .append('div')
        .attr('class', 'bar-chart-tooltip')
        .style('position', 'absolute')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('background', colors.tooltipBg)
        .style('border', `1px solid ${colors.tooltipBorder}`)
        .style('border-radius', '8px')
        .style('padding', '8px 12px')
        .style('box-shadow', '0 4px 20px rgba(0, 0, 0, 0.15)')
        .style('font-size', '11px')
        .style('font-weight', '550')
        .style('z-index', '1000')
        .style('transition', 'opacity 0.15s ease');


      // Store component reference for use in closures
      const self = this;

      // Helper function to build unified tooltip content for a category
      const buildTooltipContent = (category: string): string => {
        let tooltipHTML = `<div style="color: ${colors.tooltipText};">`;
        // Find the full date label (with year) from the first available series point
        let dateLabel = '';
        for (const series of processedSeries) {
          const point = series.data.find(p => p.category === category);
          if (point?.label) {
            dateLabel = point.label;
            break;
          }
        }

        // Category/Date header
        tooltipHTML += `<div style="font-weight: 600; margin-bottom: 6px; padding-bottom: 6px; border-bottom: 1px solid ${colors.tooltipBorder}; color: ${colors.tooltipText}">
          ${dateLabel || (self.tooltip.categoryFormatter ? self.tooltip.categoryFormatter(category) : category)}
        </div>`;

        // Add all series data for this category
        processedSeries.forEach((series) => {
          const point = series.data.find(p => p.category === category);
          if (!point) return;

          tooltipHTML += `<div style="display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 4px;">`;
          tooltipHTML += `<div style="display: flex; align-items: center; gap: 8px;">`;

          // Icon based on series type
          if (series.isLine) {
            tooltipHTML += `<span style="width: 12px; height: 2px; background: ${series.lineColor}; display: inline-block; border-radius: 1px;"></span>`;
          } else {
            tooltipHTML += `<span style="width: 10px; height: 10px; background: ${series.color}; border-radius: 50%; display: inline-block;"></span>`;
          }

          tooltipHTML += `<span style="color: ${colors.axisLabelColor}">${series.name}</span>`;
          tooltipHTML += `</div>`;
          tooltipHTML += `<span style="font-weight: 600; color: ${colors.tooltipText}">${self.formatValue(point.value, series.valueFormat)}</span>`;
          tooltipHTML += `</div>`;
        });

        tooltipHTML += `</div>`;
        return tooltipHTML;
      };

      // Unified tooltip handler
      const showTooltip = (category: string) => {
        const content = buildTooltipContent(category);
        tooltip.html(content).style('opacity', 1);
      };

      const hideTooltip = () => {
        tooltip.style('opacity', 0);
      };

      const moveTooltip = (event: MouseEvent) => {
        const [ptrX, ptrY] = d3.pointer(event, container);
        const tooltipNode = tooltip.node() as HTMLElement;
        const tooltipWidth = tooltipNode.getBoundingClientRect().width;
        const tooltipHeight = tooltipNode.getBoundingClientRect().height;

        let tooltipLeft = ptrX + 15;
        if (tooltipLeft + tooltipWidth > width) {
          tooltipLeft = ptrX - 15 - tooltipWidth;
        }

        let tooltipTop = ptrY - tooltipHeight / 2;
        if (tooltipTop < 0) tooltipTop = 0;
        if (tooltipTop + tooltipHeight > height) tooltipTop = height - tooltipHeight;

        tooltip
          .style('left', `${tooltipLeft}px`)
          .style('top', `${tooltipTop}px`);
      };

      // Add hover overlay to capture events across the entire chart area
      const overlay = svg.append('rect')
        .attr('class', 'chart-overlay')
        .attr('x', marginLeft)
        .attr('y', marginTop)
        .attr('width', Math.max(0, width - marginLeft - marginRight))
        .attr('height', Math.max(0, height - marginTop - marginBottom))
        .attr('fill', 'transparent')
        .style('pointer-events', 'all')
      // .style('cursor', '');

      // Add guided hover line
      const hoverLine = svg.append('line')
        .attr('class', 'hover-guide-line')
        .attr('stroke', colors.axisColor)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,4')
        .style('opacity', 0)
        .style('pointer-events', 'none');

      overlay
        .on('mousemove', (event: MouseEvent) => {
          const [mx, my] = d3.pointer(event, svg.node());
          const categories = isHorizontal ? (yScale as d3.ScaleBand<string>).domain() : (xScale as d3.ScaleBand<string>).domain();

          let category = '';
          let xLine: number, yLineMin: number, yLineMax: number;
          let yLine: number, xLineMin: number, xLineMax: number;

          if (isHorizontal) {
            const step = (yScale as d3.ScaleBand<string>).step();
            const index = Math.floor((my - marginTop) / step);
            category = categories[index];

            if (category) {
              const yPos = (yScale as d3.ScaleBand<string>)(category)! + (yScale as d3.ScaleBand<string>).bandwidth() / 2;
              yLine = yPos;
              xLineMin = marginLeft;
              xLineMax = width - marginRight;

              hoverLine
                .attr('x1', xLineMin).attr('x2', xLineMax)
                .attr('y1', yLine).attr('y2', yLine)
                .style('opacity', self.tooltip.showHoverLine ? 1 : 0);
            }
          } else {
            const step = (xScale as d3.ScaleBand<string>).step();
            // Important: Subtract marginLeft and also account for the step's alignment/padding
            const index = Math.floor((mx - marginLeft) / step);
            category = categories[index];

            if (category) {
              const xPos = (xScale as d3.ScaleBand<string>)(category)! + (xScale as d3.ScaleBand<string>).bandwidth() / 2;
              xLine = xPos;
              yLineMin = marginTop;
              yLineMax = height - marginBottom;

              hoverLine
                .attr('x1', xLine).attr('x2', xLine)
                .attr('y1', yLineMin).attr('y2', yLineMax)
                .style('opacity', self.tooltip.showHoverLine ? 1 : 0);
            }
          }

          if (category) {
            showTooltip(category);
            moveTooltip(event);
          } else {
            hideTooltip();
            hoverLine.style('opacity', 0);
          }
        })
        .on('mouseleave', () => {
          hideTooltip();
          hoverLine.style('opacity', 0);
        });
    }

    this.hasRenderedOnce = true;
  }

  /**
   * Get legend items for template
   */
  get legendItems(): { key: string; name: string; color: string; isLine?: boolean }[] {
    const colors = this.getThemeColors();
    return this.series.map((s, index) => ({
      key: s.key,
      name: s.name,
      color: s.isLine ? (s.lineColor || colors.seriesColors[index % colors.seriesColors.length]) : (s.color || colors.seriesColors[index % colors.seriesColors.length]),
      isLine: s.isLine,
    }));
  }

  /**
   * Check if legend should be shown
   */
  get showLegend(): boolean {
    return this.legend.show !== false && this.series.length > 0;
  }
}

